import { useState } from "preact/hooks";
import type { AssistSettings, CarParams } from "@midnight-pass/sim";
import type { Game } from "../game/game";

interface Control {
  label: string;
  min: number;
  max: number;
  step: number;
  get: (car: CarParams, assists: AssistSettings) => number;
  set: (car: CarParams, assists: AssistSettings, v: number) => void;
}

interface Group {
  title: string;
  controls: Control[];
}

const car = (
  label: string,
  min: number,
  max: number,
  step: number,
  get: (c: CarParams) => number,
  set: (c: CarParams, v: number) => void,
): Control => ({ label, min, max, step, get: (c) => get(c), set: (c, _a, v) => set(c, v) });

const assist = (label: string, key: keyof AssistSettings): Control => ({
  label,
  min: 0,
  max: 1,
  step: 0.05,
  get: (_c, a) => a[key],
  set: (_c, a, v) => {
    a[key] = v;
  },
});

const GROUPS: Group[] = [
  {
    title: "Grip",
    controls: [
      car(
        "Front grip (mu)",
        0.6,
        2,
        0.02,
        (c) => c.gripFront.mu,
        (c, v) => (c.gripFront.mu = v),
      ),
      car(
        "Rear grip (mu)",
        0.6,
        2,
        0.02,
        (c) => c.gripRear.mu,
        (c, v) => (c.gripRear.mu = v),
      ),
      car(
        "Front peak slip (°)",
        3,
        16,
        0.5,
        (c) => c.gripFront.peakSlipDeg,
        (c, v) => (c.gripFront.peakSlipDeg = v),
      ),
      car(
        "Rear peak slip (°)",
        3,
        16,
        0.5,
        (c) => c.gripRear.peakSlipDeg,
        (c, v) => (c.gripRear.peakSlipDeg = v),
      ),
      car(
        "Front falloff",
        0.2,
        1,
        0.01,
        (c) => c.gripFront.falloff,
        (c, v) => (c.gripFront.falloff = v),
      ),
      car(
        "Rear falloff",
        0.2,
        1,
        0.01,
        (c) => c.gripRear.falloff,
        (c, v) => (c.gripRear.falloff = v),
      ),
      car(
        "Power eats grip",
        0,
        1,
        0.05,
        (c) => c.combinedSlip,
        (c, v) => (c.combinedSlip = v),
      ),
    ],
  },
  {
    title: "Balance",
    controls: [
      car(
        "Weight on front",
        0.4,
        0.6,
        0.005,
        (c) => c.frontWeight,
        (c, v) => (c.frontWeight = v),
      ),
      car(
        "Weight transfer",
        0,
        3,
        0.05,
        (c) => c.weightTransferGain,
        (c, v) => (c.weightTransferGain = v),
      ),
      car(
        "Yaw inertia",
        0.1,
        0.5,
        0.005,
        (c) => c.yawInertiaFactor,
        (c, v) => (c.yawInertiaFactor = v),
      ),
      car(
        "Yaw damping",
        0,
        3,
        0.05,
        (c) => c.yawDamping,
        (c, v) => (c.yawDamping = v),
      ),
    ],
  },
  {
    title: "Handbrake & steering",
    controls: [
      car(
        "Handbrake rear grip ×",
        0.1,
        1,
        0.01,
        (c) => c.handbrake.rearGrip,
        (c, v) => (c.handbrake.rearGrip = v),
      ),
      car(
        "Steering lock (°)",
        15,
        55,
        1,
        (c) => c.steering.lockDeg,
        (c, v) => (c.steering.lockDeg = v),
      ),
      car(
        "Lock at top speed",
        0.1,
        1,
        0.01,
        (c) => c.steering.highSpeedFactor,
        (c, v) => (c.steering.highSpeedFactor = v),
      ),
      car(
        "Key steering speed",
        1,
        8,
        0.1,
        (c) => c.steering.speed,
        (c, v) => (c.steering.speed = v),
      ),
    ],
  },
  {
    title: "Assists",
    controls: [
      car(
        "Counter-steer strength",
        0,
        1,
        0.05,
        (c) => c.assists.counterSteer,
        (c, v) => (c.assists.counterSteer = v),
      ),
      car(
        "Angle cap (°)",
        25,
        90,
        1,
        (c) => c.assists.angleCapDeg,
        (c, v) => (c.assists.angleCapDeg = v),
      ),
      car(
        "Angle cap gain",
        0,
        8,
        0.1,
        (c) => c.assists.angleCapGain,
        (c, v) => (c.assists.angleCapGain = v),
      ),
      car(
        "Throttle slew (/s)",
        1,
        20,
        0.5,
        (c) => c.assists.throttleSlew,
        (c, v) => (c.assists.throttleSlew = v),
      ),
      assist("Counter-steer on (0-1)", "counterSteer"),
      assist("Angle cap on (0-1)", "angleCap"),
      assist("Throttle smoothing on (0-1)", "throttleSmoothing"),
    ],
  },
];

/** Deep copy of just the parameters, for reset and for the clipboard. */
const clone = (c: CarParams): CarParams => JSON.parse(JSON.stringify(c)) as CarParams;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API can be blocked (insecure LAN origin); fall back to a hidden textarea.
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  }
}

/**
 * Dev-only live tuning. Edits mutate the running car, so the next tick uses them. "Copy JSON" copies
 * the car's `physics` block to paste into packages/content/data/cars.json.
 */
export function TuningPanel({ game, defaults }: { game: Game; defaults: CarParams }) {
  const [, redraw] = useState(0);
  const [copied, setCopied] = useState("");
  const bump = () => redraw((n) => n + 1);

  const onCopy = async () => {
    const ok = await copyText(JSON.stringify(game.car, null, 2));
    setCopied(ok ? "Copied" : "Copy failed");
    setTimeout(() => setCopied(""), 1500);
  };

  const onReset = () => {
    Object.assign(game.car, clone(defaults));
    Object.assign(game.assists, { counterSteer: 1, angleCap: 1, throttleSmoothing: 1 });
    bump();
  };

  return (
    <div
      class="absolute overflow-y-auto font-mono text-2xs text-text-primary"
      style={{
        right: "calc(var(--safe-right) + 10px)",
        top: "calc(var(--safe-top) + 44px)",
        bottom: "calc(var(--safe-bottom) + 64px)",
        width: "300px",
        background: "rgba(5,7,10,0.86)",
        border: "1px solid var(--mp-color-neon-cyan-dim)",
        padding: "8px 10px",
        touchAction: "pan-y",
      }}
      // Keep typing in the panel from driving the car.
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div class="mb-2 flex items-center justify-between gap-2">
        <span class="font-display text-sm uppercase tracking-widest text-neon-cyan">
          Tuning (T)
        </span>
        <span class="flex gap-2">
          <button
            type="button"
            class="border border-neon-cyan px-2 py-1 text-neon-cyan"
            onClick={onCopy}
          >
            {copied || "Copy JSON"}
          </button>
          <button
            type="button"
            class="border border-text-muted px-2 py-1 text-text-secondary"
            onClick={onReset}
          >
            Reset
          </button>
        </span>
      </div>
      {GROUPS.map((group) => (
        <section key={group.title} class="mb-3">
          <h3 class="mb-1 uppercase tracking-widest text-text-secondary">{group.title}</h3>
          {group.controls.map((ctl) => {
            const value = ctl.get(game.car, game.assists);
            return (
              <label key={ctl.label} class="mb-1 block">
                <span class="flex justify-between">
                  <span>{ctl.label}</span>
                  <span class="text-neon-cyan">{value.toFixed(ctl.step < 0.1 ? 2 : 1)}</span>
                </span>
                <input
                  type="range"
                  class="w-full"
                  min={ctl.min}
                  max={ctl.max}
                  step={ctl.step}
                  value={value}
                  onInput={(e) => {
                    ctl.set(
                      game.car,
                      game.assists,
                      Number((e.currentTarget as HTMLInputElement).value),
                    );
                    bump();
                  }}
                  // Don't leave focus on the slider, or Space / arrows would move it while driving.
                  onPointerUp={(e) => (e.currentTarget as HTMLInputElement).blur()}
                />
              </label>
            );
          })}
        </section>
      ))}
      <p class="text-text-muted">
        Paste into <code>packages/content/data/cars.json</code> as the car's <code>physics</code>.
      </p>
    </div>
  );
}
